import axiosOrig from 'axios';
import { sleep } from '../util';
import { CacheManager } from './cache';
import { IdentitiesCache } from './cache/identitiesCache';
import { UserPrefCache } from './cache/prefCache';
import { addCsrfHeader, addRequestedWithHeader, addSASRequestHeader, captureCsrfHeader, createCsrfRetryHandler, createServerErrorRetryHandler, throttleGate, throttleRelease, throttleReleaseError, verifyAuthentication } from './interceptors';
import { addAcceptLocaleHeader, setAcceptLocaleHeader } from './interceptors/accept-locale-header';
// error-retry-handler interceptor depends on defaults to be defined.
// Normally test overrides should be done in a test setup. But I found that trying to setup defaults in extend-test
// ended up breaking some tests because some tests mock this file directly instead of mocking network calls. The
// alternative would have been to setup defaults in each test file itself.
const defaultConfig = process.env.NODE_ENV === 'test' ? {
  serverErrorAttemptLimit: 1,
  serverErrorTimeout: () => 0
} : {
  serverErrorAttemptLimit: 3,
  serverErrorTimeout: config => {
    const count = config.serverErrorAttemptCount;
    if (count === 1) {
      return 3000;
    }
    return 5000;
  }
};
const createNovaAxios = config => {
  const axios = axiosOrig.create({
    ...defaultConfig,
    ...config
  });
  axios.interceptors.request.use(addRequestedWithHeader);
  axios.interceptors.response.use(undefined, verifyAuthentication);
  axios.interceptors.request.use(addSASRequestHeader);

  // The order of the retry handlers and the throttle gate is meaningful here.
  // If multiple requests are going to the same service, we want the first one to get the CSRF
  // header before trying to add it to the other requests. So the gate must run before the header
  // injection. The current implementatin of axios has request interceptors run in reverse order.
  axios.interceptors.request.use(addCsrfHeader);
  axios.interceptors.response.use(undefined, createServerErrorRetryHandler(axios));
  axios.interceptors.response.use(captureCsrfHeader, createCsrfRetryHandler(axios));
  axios.interceptors.request.use(throttleGate);
  axios.interceptors.response.use(throttleRelease, throttleReleaseError);
  axios.interceptors.request.use(addAcceptLocaleHeader);
  return axios;
};

// CacheAwareAxios returns cache aware instance of axios that can route calls
// through cache, if applicable, before handing over to input axios instance.
const cacheAwareAxios = _axios => {
  let cacheCreated = false;
  const createCache = async () => {
    cacheCreated = true;
    try {
      const {
        getConfiguration
      } = await import('../internal');
      const config = await getConfiguration();
      if (config.get('nova-axios-cache') === false) {
        return;
      }
    } catch (err) {
      // ignore
    }
    CacheManager.instance.addCache(new UserPrefCache(_axios));
    CacheManager.instance.addCache(new IdentitiesCache(_axios));
  };
  // TODO this axios api should be deprecated.
  const x = (url, config) => {
    if (typeof url !== 'string') {
      return x.request(url);
    }
    if (config) {
      config.url = url;
      return x.request(config);
    }
    return x.get(url);
  };
  x.defaults = _axios.defaults;
  x.interceptors = _axios.interceptors;
  x.getUri = config => {
    return _axios.getUri(config);
  };
  x.request = config => {
    // delay cache activation until very first call so configurations can be read to disable
    // cache.
    if (!cacheCreated) {
      createCache();
    }
    const cache = CacheManager.instance.findCache(config);
    if (cache) {
      return cache.handle(config);
    }
    return _axios.request(config);
  };
  const noDataMethods = (method, url, config) => {
    const cnf = config || {};
    cnf.url = url;
    cnf.method = method;
    return x.request(cnf);
  };
  const dataMethods = (method, url, data, config) => {
    const cnf = config || {};
    cnf.url = url;
    cnf.method = method;
    cnf.data = data;
    return x.request(cnf);
  };
  x.get = (url, config) => {
    return noDataMethods('get', url, config);
  };
  x.delete = (url, config) => {
    return noDataMethods('delete', url, config);
  };
  x.head = (url, config) => {
    return noDataMethods('head', url, config);
  };
  x.options = (url, config) => {
    return noDataMethods('options', url, config);
  };
  x.post = (url, data, config) => {
    return dataMethods('post', url, data, config);
  };
  x.put = (url, data, config) => {
    return dataMethods('put', url, data, config);
  };
  x.patch = (url, data, config) => {
    return dataMethods('patch', url, data, config);
  };
  return x;
};

/**
 * Create a cache-aware instance of Nova Axios with the provided config. The config options become the default
 * for all requests made with the returned instance.
 * @param config Config to pass to initialize Axios instance
 * @returns Nova Axios instance
 */
export const createAxiosInstance = config => {
  return cacheAwareAxios(createNovaAxios(config));
};

// declare global variable to share cache across apps.

window.sas = window.sas || {};

// It is possible this file is loaded twice, so store it in the window to avoid multiple instances
// of axios being created
const testEnv = process.env.NODE_ENV === 'test';
const axios = testEnv ? createAxiosInstance() : window.sas.axios ?? createAxiosInstance();
if (!window.sas.axios && !testEnv) {
  Object.defineProperty(window.sas, 'axios', {
    get: function () {
      return axios;
    }
  });
}

// a possibility when nova code is embedded in openui app.
if (!window.sas.cacheManager) {
  // delay creation of cache manager.
  // TODO disable cache manager based on platform property in config.
  Object.defineProperty(window.sas, 'cacheManager', {
    get: function () {
      return CacheManager.instance;
    }
  });
}
export let axiosReady = Promise.resolve();

// Most tests that use axios will break if it sends this extra request.
// Some tests even seem to be affected the extra async logic ran here.
// So avoid running this in tests unless explicitly told to.
// Using runAxiosFetchInTest also requires lazy importing axios in the test.
if (!testEnv || window.sas.runAxiosFetchInTest) {
  // Fetch preferences to initialize Accept-Locale header
  // Run the fetch here since this is the only place where we will have
  // the cacheAwareAxios available. The cache is important to reduce the
  // number of duplicate calls on app startup.
  axiosReady = new Promise(async resolve => {
    await sleep(0);
    if (window.sas.axiosPreventFetchAcceptLocale) return;
    const response = await axios.get('/preferences/preferences/@currentUser?limit=32767');
    for (const setting of response.data.items) {
      if (setting.id === 'FormatLocale.DefaultLocale') {
        setAcceptLocaleHeader(setting.value);
        break;
      }
    }
    resolve();
  });
}
export default axios;
//# sourceMappingURL=Axios.js.map